/**
 * @template T
 * @typedef {"includes" | "notIncludes"} StringIncludesQualifier
 * @typedef {"in" | "notIn"} StringInQualifier
 * @typedef {"includes" | "notIncludes"} ArrayIncludesQualifier<T>
 * @typedef {"in" | "notIn"} ArrayInQualifier<T>
 * @typedef {"is" | "isNot"} EqualityQualifier<T>
 * @typedef {"min" | "max"} NumericQualifier
 * @typedef {T[]} ArrayIncludesQualification<T>
 * @typedef {string} StringIncludesQualification
 * @typedef {string} StringInQualification
 * @typedef {T} ArrayInQualification<T>
 * @typedef {T} EqualityQualification<T>
 * @typedef {number} NumericQualification
 * @typedef {{StringIncludesQualifier: StringIncludesQualification} & {StringInQualifier: StringInQualification} & {ArrayIncludesQualifier: ArrayIncludesQualification} & {ArrayInQualifier: ArrayInQualification} & {NumericQualifier: NumericQualification} & {EqualityQualifier: EqualityQualification}} QualificationDefinition
 */

/**
 * @overload
 * @param {ArrayIncludesQualifier<T>} qualifier
 * @param {ArrayIncludesQualification<T>} qualification
 * @param {T} subject
 * @returns {boolean} True if matched, false otherwise
 *
 * @overload
 * @param {ArrayInQualifier<T>} qualifier
 * @param {ArrayInQualification<T>} qualification
 * @param {T[]} subject
 * @returns {boolean} True if matched, false otherwise
 *
 * @overload
 * @param {StringIncludesQualifier} qualifier
 * @param {StringIncludesQualification} qualification
 * @param {string} subject
 * @returns {boolean} True if matched, false otherwise
 *
 * @overload
 * @param {StringInQualifier} qualifier
 * @param {StringInQualification} qualification
 * @param {string} subject
 * @returns {boolean} True if matched, false otherwise
 *
 * @overload
 * @param {EqualityQualifier<T>} qualifier
 * @param {EqualitQualification<T>} qualification
 * @param {T} subject
 * @returns {boolean} True if matched, false otherwise
 *
 * @overload
 * Match a subject against a qualification, using `qualifier` as the verb. Uses the same verbs as API qualifiers for consistency
 * @param {NumericQualifier} qualifier
 * @param {NumericQualification} qualification
 * @param {number} subject
 * @returns {boolean} True if matched, false otherwise
 */
export default function isMatchesQualification(qualifier, qualification, subject) {
  switch (qualifier) {
    case 'includes':
      return !!(subject && typeof subject.includes === 'function' && subject.includes(qualification));
    case 'notIncludes':
      return subject && typeof subject.includes === 'function' && !subject.includes(qualification);
    case "in":
      return !!(subject && typeof qualification.includes === 'function' && qualification.includes(subject));
    case 'notIn':
      return subject && typeof qualification.includes === 'function' && !qualification.includes(subject);
    case 'is':
      return subject === qualification;
    case 'isNot':
      return subject !== qualification;
    case 'min':
      return typeof qualification === 'number' && typeof subject === 'number' && subject >= qualification;
    case 'max':
      return typeof qualification === 'number' && typeof subject === 'number' && subject <= qualification;
    default: return false
  }
}
